import { yupResolver } from "@hookform/resolvers/yup"
import { useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Loader2 } from "lucide-react"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { BottomSheetModal, MessageError } from "@/components/shared"
import { Select } from "@/components/shared/ui/select"
import { Alert, AlertDescription, Button, Input, Label, Switch, Textarea } from "@/components/ui"
import {
  ADDRESS_KEYS,
  useCreateAddress,
  useDeleteAddress,
  useProvinces,
  useUpdateAddress,
  useWards
} from "@/modules/helpers/hooks"
import { schemaRequestSample } from "@/modules/helpers/schemas/select-skus.schema"
import type { ModalUpdateAddressProps } from "@/modules/types/jobs/campaign-form"

export function ModalUpdateAddress({
  visible,
  isPending: externalIsPending,
  onCompleted,
  onClose,
  addressId,
  defaultValues,
  addressCount
}: ModalUpdateAddressProps) {
  const queryClient = useQueryClient()
  // Check if this is edit mode (has defaultValues with existing address) or add mode
  const isEditMode = !!defaultValues?.provinceCode
  const isDeleted = !!addressCount && addressCount > 1

  // Initialize form
  const form = useForm({
    defaultValues: {
      address: "",
      phoneNumber: "",
      fullName: "",
      provinceCode: "",
      districtCode: "",
      wardCode: "",
      isDefault: false
    },
    resolver: yupResolver(schemaRequestSample)
  })

  // Watch form values for dependent selects
  const provinceCode = form.watch("provinceCode")

  // Load location data - wards theo tỉnh (không còn quận/huyện)
  const { data: provinces = [] } = useProvinces()
  const { data: wards = [] } = useWards(provinceCode)

  // Mutations for create/update
  const createMutation = useCreateAddress({
    onSuccess: () => {
      toast.success("Thêm địa chỉ thành công!")
      const formData = form.getValues()
      onCompleted(formData as any)
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
      onClose()
    },
    onError: () => {
      toast.error("Có lỗi xảy ra, vui lòng thử lại!")
    }
  })

  const updateMutation = useUpdateAddress({
    onSuccess: () => {
      toast.success("Cập nhật địa chỉ thành công!")
      const formData = form.getValues()
      onCompleted(formData as any)
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
      onClose()
    },
    onError: () => {
      toast.error("Có lỗi xảy ra, vui lòng thử lại!")
    }
  })

  const deleteMutation = useDeleteAddress({
    onSuccess: () => {
      toast.success("Xóa địa chỉ thành công!")
      onClose()
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
    },
    onError: () => {
      toast.error("Có lỗi xảy ra, vui lòng thử lại!")
    }
  })

  const isPending =
    externalIsPending ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  const handleDelete = () => {
    if (!addressId) return
    if (window.confirm("Bạn có chắc chắn muốn xóa địa chỉ này?")) {
      deleteMutation.mutate(addressId)
    }
  }

  // Reset form when modal opens with defaultValues
  useEffect(() => {
    if (visible && defaultValues) {
      form.reset({
        address: defaultValues.address || "",
        phoneNumber: defaultValues.phoneNumber || "",
        fullName: defaultValues.fullName || "",
        provinceCode: defaultValues.provinceCode || "",
        districtCode: "",
        wardCode: defaultValues.wardCode || "",
        isDefault: defaultValues.isDefault || false
      })
    } else if (!visible) {
      form.reset({
        address: "",
        phoneNumber: "",
        fullName: "",
        provinceCode: "",
        districtCode: "",
        wardCode: "",
        isDefault: false
      })
    }
  }, [visible, defaultValues, form])

  // Set wardCode when wards are loaded and we have a default wardCode
  useEffect(() => {
    if (
      visible &&
      defaultValues?.wardCode &&
      wards.length > 0 &&
      provinceCode === defaultValues.provinceCode
    ) {
      const wardExists = wards.some((w) => w.code === defaultValues.wardCode)
      if (wardExists && form.getValues("wardCode") !== defaultValues.wardCode) {
        form.setValue("wardCode", defaultValues.wardCode)
      }
    }
  }, [visible, defaultValues, wards, provinceCode, form])

  // Reset ward when province changes
  useEffect(() => {
    if (provinceCode && visible) {
      const currentWard = form.getValues("wardCode")
      const wardExists = wards.some((w) => w.code === currentWard)
      if (currentWard && !wardExists) {
        form.setValue("wardCode", "")
      }
    }
  }, [provinceCode, visible, wards, form])

  const handleSubmit = async (data: any) => {
    const payload = {
      address: data.address,
      phoneNumber: data.phoneNumber,
      fullName: data.fullName,
      provinceCode: data.provinceCode,
      wardCode: data.wardCode,
      isDefault: data.isDefault
    }

    if (isEditMode && addressId) {
      updateMutation.mutate({ id: addressId, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  return (
    <BottomSheetModal
      isVisible={visible}
      setIsVisible={onClose}
      title={isEditMode ? "Cập nhật địa chỉ nhận hàng" : "Thêm địa chỉ nhận hàng"}
    >
      <div className="p-4">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {form.formState.errors.warning && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{form.formState.errors.warning.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label required>Họ và tên</Label>
            <Controller
              control={form.control}
              name="fullName"
              render={({ field, fieldState }) => (
                <MessageError error={fieldState.error?.message}>
                  <Input
                    placeholder="Nhập họ tên đầy đủ"
                    autoFocus={false}
                    {...field}
                    className="rounded-xl"
                  />
                </MessageError>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Số điện thoại (Zalo)</Label>
            <Controller
              control={form.control}
              name="phoneNumber"
              render={({ field, fieldState }) => (
                <MessageError error={fieldState.error?.message}>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nhập số điện thoại"
                      {...field}
                      inputMode="numeric"
                      autoFocus={false}
                      className="rounded-xl flex-1"
                    />
                  </div>
                </MessageError>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Tỉnh/Thành phố</Label>
            <Controller
              control={form.control}
              name="provinceCode"
              render={({ field, fieldState }) => (
                <MessageError error={fieldState.error?.message}>
                  <Select
                    value={field.value || ""}
                    onChange={field.onChange}
                    options={provinces.map((province) => ({
                      label: province.name,
                      value: province.code
                    }))}
                    placeholder="Chọn tỉnh/thành phố"
                    className="w-full rounded-xl !h-12"
                  />
                </MessageError>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Phường/Xã</Label>
            <Controller
              control={form.control}
              name="wardCode"
              render={({ field, fieldState }) => (
                <MessageError error={fieldState.error?.message}>
                  <Select
                    value={field.value || ""}
                    onChange={field.onChange}
                    options={wards.map((ward) => ({
                      label: ward.name,
                      value: ward.code
                    }))}
                    placeholder="Chọn phường/xã"
                    disabled={!provinceCode}
                    className="w-full rounded-xl !h-12"
                  />
                </MessageError>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Tên đường, tòa nhà, số nhà</Label>
            <Controller
              control={form.control}
              name="address"
              render={({ field, fieldState }) => (
                <MessageError error={fieldState.error?.message}>
                  <Textarea
                    placeholder="Nhập tên đường, tòa nhà, số nhà"
                    {...field}
                    className="rounded-xl text-sm min-h-[80px]"
                  />
                </MessageError>
              )}
            />
          </div>

          <div className="flex items-center justify-between gap-3 py-2">
            <Label
              className="font-normal cursor-pointer"
              onClick={() => form.setValue("isDefault", !form.watch("isDefault"))}
            >
              Đặt làm mặc định
            </Label>
            <Controller
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <Switch checked={field.value || false} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          <div className="flex gap-2">
            {isDeleted && (
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={handleDelete}
                className="w-[130px] border bg-white border-red-500 !h-12 rounded-xl text-red-500"
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Xóa địa chỉ
              </Button>
            )}
            <Button
              type="submit"
              disabled={isPending}
              className={`!h-12 transition-transform flex-1 rounded-xl bg-gradient-primary`}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lưu
            </Button>
          </div>
        </form>
      </div>
    </BottomSheetModal>
  )
}
